import React, { useEffect, useMemo, useState } from "react";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import TableContainer from "../../components/Common/TableContainer";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  CardText,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import axios from "axios";

const AllScans = () => {
  document.title = "Assets Overview";
  const [jobs, setJobs] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [job, setJob] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [taskDetails, setTaskDetails] = useState(null);

  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;

  // Function to calculate stats from jobs data
  const calculateJobStats = (jobsData) => {
    if (!jobsData.length) return null;

    return [
      {
        title: "Active Scans",
        description: "Currently running Scans",
        id: 1,
        number: jobsData.filter(job => job.status === "Active").length
      },
      {
        title: "Pending Scans",
        description: "Scans awaiting action",
        id: 2,
        number: jobsData.filter(job => job.status === "New").length
      },
      {
        title: "Completed Scans",
        description: "Successfully finished Scans",
        id: 3,
        number: jobsData.filter(job => job.status === "Completed" || job.status === "Analyzed").length
      },
      {
        title: "Total Scans",
        description: "All Scans",
        id: 4,
        number: jobsData.length
      }
    ];
  };

  const onClickView = (task) => {
    setTaskDetails(task);
    setViewModal(true);
  };

  const refreshData = async () => {
    setIsLoadingData(true);
    try {
      const response = await axios.get(`https://api.bluhawk.live/allscans/${userId}`);
      setJobs(response.data);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const onClickDelete = async (jobData) => {
    if (jobData && jobData.id && jobData.category) {
      try {
        const response = await fetch(`https://api.bluhawk.live/trash/${userId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            scanId: jobData.id,
            category: jobData.category,
          }),
        });

        if (response.ok) {
          // Update UI by filtering out the deleted job
          setJobs((prevJobs) => prevJobs.filter((j) => j.id !== jobData.id));
        } else {
          console.error("Failed to delete the job.");
        }
      } catch (error) {
        console.error("Error deleting job:", error);
      }
    } else {
      console.error("Job or required fields are missing!");
    }
  };


  const EmptyState = () => (
    <Card>
      <div className="text-center p-5">
        <div className="mb-4">
          <i className="mdi mdi-file-search text-primary" style={{ fontSize: "4rem" }} />
        </div>
        <h3 className="mb-3 font-weight-bold">No Insights Available Yet</h3>
        <p className="text-muted mb-4">
          It seems like no data is available at the moment. This could be due to recent activity or a delay in data updates.
          You can start by initiating scans or reviewing your recent actions.
        </p>
        <div className="d-flex justify-content-center gap-3">
          <Link to="/addentities" className="btn btn-primary px-4 py-2">
            Analyze
          </Link>
        </div>
      </div>
    </Card>

  );


  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          return (
            <Link to="#" className="text-body fw-bold">
              {cellProps.row.original.id}
            </Link>
          );
        },
      },
      {
        header: "NAME",
        accessorKey: "assetTitle",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Compliance",
        accessorKey: "category",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Impact",
        accessorKey: "type",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          switch (cellProps.row.original.type) {
            case "CRITICAL":
              return <span className="badge badge-soft-danger">CRITICAL</span>;
            case "HIGH":
              return <span className="badge badge-soft-warning">HIGH</span>;
            case "MEDIUM":
              return <span className="badge badge-soft-orange">MEDIUM</span>;
            default:
              return <span className="badge badge-soft-info">LOW</span>;
          }
        },
      },
      {
        header: "Uploaded on",
        enableColumnFilter: false,
        enableSorting: true,
        accessorKey: "postedDate",
      },
      {
        header: "Processed on",
        enableColumnFilter: false,
        enableSorting: true,
        accessorKey: "lastDate",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          switch (cellProps.row.original.status) {
            case "Active":
              return <Badge className="bg-success">Active</Badge>;
            case "Close":
              return <Badge className="bg-danger">Close</Badge>;
            case "UnApproved":
              return <Badge className="bg-danger">UnApproved</Badge>;
            case "Approved":
              return <Badge className="bg-success">Approved</Badge>;
            case "In Progress":
              return <Badge className="bg-info">In Progress</Badge>;
            case "Completed":
            case "Analyzed":
              return <Badge className="bg-success">Analyzed</Badge>;
            default:
              return <Badge className="bg-info">New</Badge>;
          }
        },
      },
      {
        header: "Assess",
        accessorKey: "resolved",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const { resolved, id, category } = cellProps.row.original;
      
          // Optimistic update state
          const [isResolved, setIsResolved] = useState(resolved);
      
          const toggleResolvedStatus = async () => {
            const newResolvedStatus = !isResolved;
            setIsResolved(newResolvedStatus); // Optimistic UI update
      
            // Determine the correct endpoint
            let endpoint = "";
            if (category === "Vulnerability Scan") {
              endpoint = "/api/vulnerability-scan";
            } else if (category === "Malware Scan") {
              endpoint = "/malware-scan";
            } else if (category === "Threat Modeling") {
              endpoint = "/threat-modeling";
            }
      
            try {
              // Send the backend update request
              const response = await fetch(`https://api.bluhawk.live${endpoint}/${userId}/results/${id}`, {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ scanId: id, resolved: newResolvedStatus }),
              });
      
              if (!response.ok) {
                console.error("Failed to update resolved status:", response.statusText);
                // If the update fails, revert the resolved status in UI
                setIsResolved(resolved);
              }
            } catch (error) {
              console.error("Error updating resolved status:", error);
              // Revert in case of error
              setIsResolved(resolved);
            }
          };
          if (resolved === null || resolved === undefined) {
            return <span>-</span>;
          }
      
          return (
            <Button
              color={isResolved ? "success" : "danger"}
              size="sm"
              onClick={toggleResolvedStatus}
              disabled={isResolved === null || isResolved === undefined}
            >
              {isResolved ? "Apply" : "Withdrew"}
            </Button>
          );
        },
      },
      
      {
        header: "Source",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          const jobData = cellProps.row.original;
          const jobId = jobData.id;

          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <li data-bs-toggle="tooltip" data-bs-placement="top" title="View">
                {jobData.category === "Vulnerability Scan" ? (
                  <Link
                    to={`/scanresults/${jobId}`}
                    className="btn btn-sm btn-soft-primary"
                    id={`viewtooltip-${jobId}`}
                  >
                    <i className="mdi mdi-eye-outline" />
                  </Link>
                ) : jobData.category === "Malware Scan" ? (
                  <Link
                    to={`/malware/${jobId}`}
                    className="btn btn-sm btn-soft-primary"
                    id={`viewtooltip-${jobId}`}
                  >
                    <i className="mdi mdi-eye-outline" />
                  </Link>
                ) : (
                  <Link
                    to="#"
                    className="btn btn-sm btn-soft-primary"
                    onClick={() => onClickView(jobData)}
                    id={`viewtooltip-${jobId}`}
                  >
                    <i className="mdi mdi-eye-outline" />
                  </Link>
                )}
              </li>
              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => onClickDelete(jobData)}
                  id={`deletetooltip-${jobId}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </Link>
              </li>
            </ul>
          );
        },
      }
      ,
    ],
    []
  );

  const jobStats = calculateJobStats(jobs);

  return (
    <React.Fragment>


      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs breadcrumbItem="Scans" />

          {/* Stats Cards - Only show if there's data */}
          {jobStats && (
            <Row className="mb-4">
              {jobStats.map((stat) => (
                <Col lg={3} md={6} sm={12} key={stat.id} className="mb-4">
                  <Card>
                    <CardBody>
                      <CardTitle tag="h5" className="mb-3">
                        {stat.title}
                      </CardTitle>
                      <CardText>{stat.description}</CardText>
                      <div
                        style={{
                          fontSize: "3rem",
                          fontWeight: "bold",
                          color: "#007bff",
                        }}
                      >
                        {stat.number}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">Uploads</h5>
                    <div className="flex-shrink-0">
                      <Link
                        to="#!"
                        className="btn btn-light me-1"
                        onClick={refreshData}
                      >
                        <i className="mdi mdi-refresh"></i>
                      </Link>
                    </div>
                  </div>
                </CardBody>

                <CardBody>
                  {isLoadingData ? (
                    <div className="text-center">
                      <Spinners setLoading={setIsLoadingData} />
                    </div>
                  ) : jobs.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={jobs}
                      isCustomPageSize={true}
                      isGlobalFilter={true}
                      isJobListGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search for ..."
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(!viewModal)}
        size="lg"
        style={{ maxWidth: "80%", width: "80%", height: "80%", margin: "auto" }}
        centered
      >
        <ModalHeader toggle={() => setViewModal(false)}>
          {taskDetails ? taskDetails.assetTitle : "Loading..."}
        </ModalHeader>
        <ModalBody style={{ height: "70vh", overflowY: "auto" }}>
          {taskDetails ? (
            <div>
              <p><strong></strong> {taskDetails.description}</p>
              <p><strong>Initiated:</strong> {taskDetails.postedDate}</p>
              <p><strong>Last Scan:</strong> {taskDetails.lastDate}</p>
              <div>
                <strong>Analysis:</strong>
                {taskDetails.analysis && taskDetails.analysis.trim() !== "" ? (
                  <p>{taskDetails.analysis}</p>
                ) : (
                  <p className="text-warning">Analysis is not complete yet.</p>
                )}
              </div>
            </div>
          ) : (
            <div>Loading...</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default AllScans;